const singleton = Symbol('property of instance')
const singletonEnforcer = Symbol('to verify singleton')

export const INFACE_EVENT_TYPE = {
  TOY_WEB_SDK_MODULE: 'toy-web-sdk:module', // 3rd sdks 간에 주고 받는 event 로 대외공개하지 않습니다.
  TOY_WEB_SDK_EVENT: 'toy-web-sdk:event', // 외부에 공개하는 이벤트 ex: game-token 생성, gameAuth 초기화 완료 등
}

// v2 에서 사용하는 meta name 입니다.
export const INFACE_META_NAME = {
  INFACE_GID: 'inface-gid',
  INFACE_ENV: 'inface-env',
}

export const SDK_STATUS = {
  INITIALIZED: 'initialized',
  PREPARING: 'preparing',
  FINALIZED: 'finalized',
}

/**
 * @typedef ToyWebSdkEvent
 * @property {string} moduleName - ex: web-auth | web-game-auth
 * @property {object} eventData
 * @property {string} eventData.type - 모듈별로 정의해서 사용합니다. ex: lifecycle
 * @property {object} eventData.data - object type 을 권장하며, module 별 정책
 */

export class InfaceService {
  /** @returns {InfaceService} */
  static get instance() {
    if (!this[singleton]) this[singleton] = new InfaceService(singletonEnforcer)
    return this[singleton]
  }

  get moduleName() {
    return this._moduleName
  }

  /**
   *  TOY Web SDK 의 meta tag 들을 반환합니다.
   *
   * - TOY Web SDK v2 정책: <meta name="" content="">
   * - TOY Web SDK v3 정책: <meta type="inface" module="${module}" ...>
   *
   * v3 부터는 각 module 에서 임의로 meta element 의 attribute 를 정의합니다.
   * 정책 변경에 따라 반환값은 다음과 같습니다.
   *
   * 1. v2 에서 사용한 메타 태그
   *  - infaceMetas.v2 object 에 { name: content } 형태로 포함됩니다.
   *  - 'inface-gid' property 는 필수로 포함되어야 합니다.
   * 2. v3 부터 적용하는 메타 태그
   *  - type, module attribute 외에는 module 별 선언이라 meta tag DOM 을 반환합니다.
   *  - module 별 여러개의 meta 를 활용 할 수 있어서 array 형태( moduleName: [] )로 반환합니다.
   *
   * @returns {{ v2: { [name: string]: string }, [moduleName: string]: [] } }}
   */
  get infaceMetas() {
    const metas = Array.from(document.querySelectorAll(`meta[type="inface"]`)).reduce((obj, meta) => {
      const moduleName = meta.getAttribute('module')
      if (!moduleName) return obj
      if (!obj[moduleName]) obj[moduleName] = []
      obj[moduleName].push(meta)
      return obj
    }, {})

    const oldMetaObj = Array.from(document.querySelectorAll(`meta[name^="inface"]`)).reduce((obj, meta) => {
      obj[meta.name] = meta.content
      return obj
    }, {})
    metas.v2 = oldMetaObj

    return metas
  }

  constructor(enforcer) {
    if (enforcer !== singletonEnforcer) {
      throw new Error('You are supposed to use InfaceService as singleton')
    }

    this._eventListeners = {}
    window.addEventListener(
      INFACE_EVENT_TYPE.TOY_WEB_SDK_MODULE,
      /**
       *
       * @param {ToyWebSdkEvent} event
       */
      event => {
        const { moduleName, eventData } = event?.detail || {}
        console.debug(`[InfaceService] toy-web-sdk:module event\n${JSON.stringify({ moduleName, eventData }, null, 2)}`)
        const callbacks = this._eventListeners[INFACE_EVENT_TYPE.TOY_WEB_SDK_MODULE]
        if (!Array.isArray(callbacks)) return

        callbacks.forEach(callback => {
          if (typeof callback === 'function') callback({ moduleName, eventData })
        })
      },
    )
  }

  setModuleName(moduleName) {
    this._moduleName = moduleName
  }

  /**
   * @param {INFACE_EVENT_TYPE} eventType
   */
  addEventListener(eventType, callback) {
    console.debug('addEventListener', eventType)
    if (typeof callback !== 'function') return
    if (!Array.isArray(this._eventListeners[eventType])) this._eventListeners[eventType] = []

    const listeners = this._eventListeners[eventType]
    if (listeners.some(listener => listener === callback)) {
      console.debug(`Do not add duplicated listener for ${eventType}`)
      return
    }

    this._eventListeners[eventType].push(callback)
  }

  /**
   *  TOY Web SDK 의 각 SDK 들의 lifecycle 변경에 따른 이벤트 전파입니다.
   * preparing -> initialized -> finalized 순서로 변경 됩니다.
   * - preparing: SDK 의 main class 가 생성된 상태입니다.
   * - initialized: SDK 의 초기화 비즈니스 로직이 완료 되어 사용 준비가 완료 된 상태입니다.
   * - finalized: SDK lifecycle 이 종료 된 상태입니다. 재사용하기 위해서는 SDK 초기화를 다시해야 합니다.
   *
   * event format
   * { type: 'lifecycle', data: { status: 'preparing'|'initialized'|'finalized' } }
   * @param {object} params
   * @param {string} params.moduleName
   * @param {string} params.status
   * @param {object} params.additionalData - status 와 동일 depth 에 추가되어 전달될 추가 data
   */
  sendStatusChangeEvent({ moduleName = this.moduleName, status = SDK_STATUS.INITIALIZED, additionalData }) {
    const eventData = {
      type: 'lifecycle',
      data: { ...additionalData, status },
    }

    this.sendModuleEvent({ moduleName, eventData })
    this.sendSdkEvent({ moduleName, eventData })
  }

  /**
   *  TOY Web SDK 모듈간에 주고받을 Event 로 외부로 공개하지 않는 이벤트입니다.
   * dependency 가 걸리는 모듈이 이벤트를 통해 활용 할 수 있습니다.
   *
   * ex) web-push 가 web-auth 초기화 완료 시점에 web-auth lifecycle initialized 이벤트를 수신 한 후,
   * 유저의 로그인 여부를 파악해서 livestream 세션을 생성 할 수 있음
   *
   * @param {ToyWebSdkEvent} obj
   */
  sendModuleEvent({ moduleName = this.moduleName, eventData }) {
    window.dispatchEvent(
      new CustomEvent(INFACE_EVENT_TYPE.TOY_WEB_SDK_MODULE, {
        detail: {
          moduleName,
          eventData,
        },
      }),
    )
  }

  /**
   *  TOY Web SDK 를 사용하는 곳에 전달할 일반 이벤트 입니다.
   *
   * ex) 웹게임에서 유저가 로그아웃 되는 경우 game-auth 로 부터 로그아웃 이벤트를 받아 비로그인 상태로 변경 할 수 있음
   *
   * @param {ToyWebSdkEvent}
   */
  sendSdkEvent({ moduleName = this.moduleName, eventData }) {
    window.dispatchEvent(
      new CustomEvent(INFACE_EVENT_TYPE.TOY_WEB_SDK_EVENT, {
        detail: {
          moduleName,
          eventData,
        },
      }),
    )
  }
}
